/**
 * The one path from a spec to pixels.
 *
 * Every entity is rendered in two layers, because isometric occlusion demands
 * it: every structure's GROUND layer (foundation pad, contact shadow, selection
 * rim) must be under every structure's BODY, not just its own. Splitting them
 * here is also what lets the Footprint and Height laws be checked on the body
 * alone — a pad legitimately covers the whole tile, a silhouette must not.
 *
 * Interaction states are DERIVED, never hand-authored (doc 10's table is a set
 * of transforms). Six entities cannot drift apart on what "hover" looks like if
 * none of them draws hover.
 */

import { groundRamp, OUTLINE, OUTLINE_HOVER, ruinRamp, SEMANTIC } from '../render/palette.ts';
import { hex, PixelSurface, type Color } from '../render/surface.ts';
import { TH, TW } from '../render/iso.ts';
import { rampFor } from './materials.ts';
import type { BuildContext, EntitySpec, EntityState } from './entity.ts';
import {
  applyOutline,
  diamondSpansH,
  ditherOverlay,
  isoDiamond,
  isoDiamondRim,
  isoDisc,
  isoPrism,
  multiplyAlpha,
  newEntityCanvas,
  toSprite,
  type EntityCanvas,
  type Sprite,
} from '../render/primitives.ts';

export interface EntityRender {
  /** Pad, contact shadow, selection rim. Drawn under every body in the scene. */
  ground: Sprite;
  /** The volumes, outlined. Subject to Laws L2 and L3. */
  body: Sprite;
}

const HOVER_LIFT = 1;

export function buildEntity(spec: EntitySpec, ctx: BuildContext): EntityRender {
  const n = spec.size;
  const pad = 10;
  const bodyCanvas = newEntityCanvas(n, spec.height + 12, pad);

  if (ctx.state === 'destroyed') drawRuin(bodyCanvas, spec, ctx);
  else spec.draw(bodyCanvas, ctx);

  applyStateTransforms(bodyCanvas.s, ctx);
  applyOutline(bodyCanvas.s, ctx.state === 'hover' ? OUTLINE_HOVER : OUTLINE);
  if (ctx.state === 'ghost') multiplyAlpha(bodyCanvas.s, 0.6);

  const body =
    ctx.state === 'hover' ? liftSurface(bodyCanvas, HOVER_LIFT) : toSprite(bodyCanvas, n);

  return { ground: buildGround(spec, ctx), body };
}

/** Pad + contact shadow +, when selected, the rim and corner notches (doc 10). */
function buildGround(spec: EntitySpec, ctx: BuildContext): Sprite {
  const n = spec.size;
  const pad = 10;
  const halfH = TH * n;
  const halfW = TW * n;
  const s = new PixelSurface(halfW * 2 + pad * 2, halfH * 2 + pad * 2);
  const c: EntityCanvas = { s, ax: halfW + pad, ay: halfH + pad };
  const g = groundRamp(ctx.depth);

  // Contact shadow first: a soft ellipse so nothing looks like a floating sticker.
  isoDisc(s, c.ax, c.ay + 1, halfW - 2, g.contactShadow);
  // Foundation pad: states which tiles this building owns.
  isoDiamond(s, c.ax, c.ay, halfH, g.padFill);
  isoDiamondRim(s, c.ax, c.ay, halfH, g.padRim);

  if (ctx.state === 'selected') {
    isoDiamondRim(s, c.ax, c.ay, halfH, OUTLINE_HOVER);
    drawCornerNotches(s, c.ax, c.ay, halfH, SEMANTIC.ink);
  } else if (ctx.state === 'hover') {
    isoDiamondRim(s, c.ax, c.ay, halfH, OUTLINE_HOVER, true, 0);
  } else if (ctx.state === 'ghost') {
    isoDiamondRim(s, c.ax, c.ay, halfH, SEMANTIC.good);
    multiplyAlpha(s, 0.6);
  }

  return { surface: s, ax: c.ax, ay: c.ay, n, ops: s.ops };
}

/** White corner notches at the four diamond corners — the selection marker. */
function drawCornerNotches(s: PixelSurface, cx: number, cy: number, halfH: number, color: Color): void {
  const spans = diamondSpansH(cx, cy, halfH);
  const first = spans[0]!;
  const last = spans[spans.length - 1]!;
  const widest = spans[halfH - 1]!;
  const marks: Array<[number, number]> = [
    [first.x0, first.y],
    [first.x1, first.y],
    [last.x0, last.y],
    [last.x1, last.y],
    [widest.x0, widest.y],
    [widest.x1, widest.y],
    [widest.x0, widest.y + 1],
    [widest.x1, widest.y + 1],
  ];
  for (const [x, y] of marks) s.px(x, y, color);
}

/** Shift a body up by `dy` px relative to its anchor — the 1 px hover lift. */
function liftSurface(c: EntityCanvas, dy: number): Sprite {
  const out = new PixelSurface(c.s.width, c.s.height);
  out.blit(c.s, 0, -dy);
  return { surface: out.resetOps(), ax: c.ax, ay: c.ay, n: 0, ops: c.s.ops };
}

/**
 * State overlay colours. Opaque on purpose: an alpha overlay would blend into
 * new colours on every surface it touched and blow past Law L5's 12-colour cap
 * with pixels nobody chose.
 */
export const STATE_COLORS = {
  brownout: hex('#08161c'),
  crack: hex('#0a1a20'),
  warning: SEMANTIC.critical,
} as const;

function applyStateTransforms(s: PixelSurface, ctx: BuildContext): void {
  const state = ctx.state;
  if (state === 'starved') {
    // Brownout: 50% checkerboard in the structure's own deepest shade. A flat
    // near-black here erased the silhouette — the building has to read as DIM,
    // not as a hole in the world.
    ditherOverlay(s, rampFor(ctx.depth).deep, 0);
  } else if (state === 'damaged') {
    // Dithered crack overlay: deterministic speckle, never random per frame.
    const before = s.ops;
    for (let y = 0; y < s.height; y++) {
      for (let x = 0; x < s.width; x++) {
        if (s.alphaAt(x, y) === 0) continue;
        if ((x * 7 + y * 13) % 11 === 0) s.px(x, y, STATE_COLORS.crack);
        else if ((x * 5 + y * 3) % 23 === 0) s.px(x, y, STATE_COLORS.warning);
      }
    }
    s.ops = before + 1;
  }
}

/** Ruins are generic on purpose: doc 03 gives every ruin the same rebuild rule. */
function drawRuin(c: EntityCanvas, spec: EntitySpec, ctx: BuildContext): void {
  const n = spec.size;
  const rubble = ruinRamp(ctx.depth.structureBase);
  const inset = Math.max(1, TH * n - 6);
  isoPrism(c, { n, inset, height: 3, ramp: rubble });
  // A few broken uprights, deterministic per footprint.
  for (let k = 0; k < n * 3; k++) {
    const dx = ((k * 11) % (TW * n)) - (TW * n) / 2;
    const dy = Math.round(dx / 2) * (k % 2 === 0 ? 1 : -1);
    const h = 2 + (k % 3);
    c.s.fillRect(c.ax + Math.round(dx / 2), c.ay + Math.round(dy / 2) - 3 - h, 2, h, rubble.left);
  }
}
